/**
 * Display list → SVG markup (editor / sheet previews).
 *
 * Same coordinates as the PDF (millimeters, y down, `viewBox` = page), so the
 * preview is the print. Fonts are referenced by CSS family names the caller
 * registers with `@font-face` from the same font bytes the PDF embeds;
 * kerning is turned off because pdf-lib does not apply it either.
 */

import { printColorToCss, type PrintColor, type StrokeStyle } from 'shared-types';
import type { DisplayItem, DisplayPage, PathCommand } from '../displayList';

export interface SvgFontStyle {
  family: string;
  weight: 'normal' | 'bold';
  style: 'normal' | 'italic';
}

export interface SvgOptions {
  /** CSS font of a registry face key. */
  font(key: string): SvgFontStyle;
  /** URL (object URL, data URL, SAS link) of an image key; null = not drawn. */
  imageHref(key: string): string | null;
  /** Id prefix for clip paths (unique per SVG on the page). */
  idPrefix?: string;
  /** Page background; omit for transparent. */
  background?: string;
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function num(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

function pathData(commands: readonly PathCommand[]): string {
  return commands
    .map((c) => {
      switch (c.op) {
        case 'M':
          return `M${num(c.x)} ${num(c.y)}`;
        case 'L':
          return `L${num(c.x)} ${num(c.y)}`;
        case 'C':
          return `C${num(c.x1)} ${num(c.y1)} ${num(c.x2)} ${num(c.y2)} ${num(c.x)} ${num(c.y)}`;
        case 'Z':
          return 'Z';
      }
    })
    .join('');
}

function strokeAttrs(stroke: StrokeStyle | null): string {
  if (!stroke) return ' stroke="none"';
  const dash = stroke.dashMm.length > 0 ? ` stroke-dasharray="${stroke.dashMm.map(num).join(' ')}"` : '';
  // A 0 mm pen is the device's thinnest line in PDF; show it as a hairline here.
  const width = stroke.weightMm > 0 ? `stroke-width="${num(stroke.weightMm)}"` : 'stroke-width="1" vector-effect="non-scaling-stroke"';
  return ` stroke="${printColorToCss(stroke.color)}" ${width} stroke-linecap="${stroke.cap}" stroke-linejoin="${stroke.join}"${dash}`;
}

function fillAttr(fill: PrintColor | null): string {
  return fill ? ` fill="${printColorToCss(fill)}"` : ' fill="none"';
}

class SvgWriter {
  private clipCount = 0;
  readonly defs: string[] = [];

  constructor(private readonly options: SvgOptions) {}

  items(items: readonly DisplayItem[]): string {
    return items.map((item) => this.item(item)).join('');
  }

  private item(item: DisplayItem): string {
    switch (item.type) {
      case 'path':
        if (!item.stroke && !item.fill) return '';
        return `<path d="${pathData(item.commands)}"${fillAttr(item.fill)}${item.fillRule === 'evenodd' ? ' fill-rule="evenodd"' : ''}${strokeAttrs(item.stroke)}/>`;
      case 'text': {
        const font = this.options.font(item.font);
        const rotate = item.rotationDeg ? ` transform="rotate(${num(item.rotationDeg)} ${num(item.x)} ${num(item.y)})"` : '';
        const spacing = item.letterSpacingMm ? ` letter-spacing="${num(item.letterSpacingMm)}"` : '';
        return `<text x="${num(item.x)}" y="${num(item.y)}" font-family="${esc(font.family)}" font-weight="${font.weight}" font-style="${font.style}" font-size="${num(item.sizeMm)}" fill="${printColorToCss(item.color)}" style="font-kerning:none;white-space:pre"${spacing}${rotate}>${esc(item.text)}</text>`;
      }
      case 'image': {
        const href = this.options.imageHref(item.image);
        if (!href) return '';
        return `<image href="${esc(href)}" x="${num(item.x)}" y="${num(item.y)}" width="${num(item.w)}" height="${num(item.h)}" preserveAspectRatio="none"/>`;
      }
      case 'group': {
        const transform = item.transform ? ` transform="matrix(${item.transform.map(num).join(' ')})"` : '';
        let clip = '';
        if (item.clip) {
          const id = `${this.options.idPrefix ?? 'pe'}-clip-${this.clipCount++}`;
          this.defs.push(`<clipPath id="${id}"><rect x="${num(item.clip.x)}" y="${num(item.clip.y)}" width="${num(item.clip.w)}" height="${num(item.clip.h)}"/></clipPath>`);
          clip = ` clip-path="url(#${id})"`;
        }
        // The clip is in the group's own coordinates: transform outside, clip inside.
        const inner = clip ? `<g${clip}>${this.items(item.items)}</g>` : this.items(item.items);
        return `<g${transform}>${inner}</g>`;
      }
    }
  }
}

export function renderSvg(page: DisplayPage, options: SvgOptions): string {
  const writer = new SvgWriter(options);
  const body = writer.items(page.items);
  const background = options.background ? `<rect width="${num(page.widthMm)}" height="${num(page.heightMm)}" fill="${esc(options.background)}"/>` : '';
  const defs = writer.defs.length > 0 ? `<defs>${writer.defs.join('')}</defs>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${num(page.widthMm)}mm" height="${num(page.heightMm)}mm" viewBox="0 0 ${num(page.widthMm)} ${num(page.heightMm)}">${defs}${background}${body}</svg>`;
}
